/**
 * AnimGraph — the anim-BP equivalent, pure TS (no three.js; the mixer bridge applies results).
 *
 * Shape: a tree of nodes evaluated against typed params each frame.
 *  - clip:     a leaf animation, optional live timeScale
 *  - blend1d:  a blendspace — piecewise-linear weights between neighbours by a scalar param
 *  - select:   a state machine — picks one child by key; children crossfade with per-target
 *              fade durations (fast into reactive states like jump, soft into locomotion)
 * Plus SLOTS: one-shot overlays with fade-in/hold/fade-out envelopes (the montage equivalent) —
 * a playing slot scales the base pose down by its own weight.
 *
 * All smoothing state lives in GraphRuntime, keyed by node path, so evaluation stays a pure
 * function of (definition, runtime, params, dt) and tests can drive it headlessly.
 */
export interface AnimParams {
  speed: number;
  grounded: boolean;
  vy: number;
  hovering: boolean;
  rolling: boolean;
  /** Roll duration (s) — the bridge fits tuck/spin timing to the mechanic. */
  rollDuration: number;
  /** 0→1 progress through the roll (0 when not rolling) — drives the dive/tuck/recover timeline. */
  rollProgress01: number;
  /** Physics rise time (s) — the bridge fits the rise clip so clip apex == physics apex. */
  jumpTimeToApex: number;
  maxWalkSpeed: number;
  maxRunSpeed: number;
}

export interface PoseTask {
  clip: string;
  weight: number;
  timeScale: number;
}

export type GraphNode =
  | { kind: 'clip'; clip: string; timeScale?: (p: AnimParams) => number }
  | {
      kind: 'blend1d';
      param: (p: AnimParams) => number;
      points: Array<{ at: number; node: GraphNode }>;
    }
  | {
      kind: 'select';
      choose: (p: AnimParams) => string;
      children: Record<string, GraphNode>;
      /** Crossfade duration (s) INTO each child; default 0.15. */
      fades?: Record<string, number>;
    };

export interface GraphRuntime {
  /** Smoothed select weights, keyed by "<nodePath>:<childKey>". */
  selectWeights: Map<string, number>;
}

export const createRuntime = (): GraphRuntime => ({ selectWeights: new Map() });

function evalNode(
  node: GraphNode,
  path: string,
  rt: GraphRuntime,
  p: AnimParams,
  dt: number,
  weight: number,
  out: PoseTask[],
): void {
  if (weight <= 1e-4) {
    // Still advance select smoothing toward 0 for hidden branches next time they're seen.
    return;
  }
  switch (node.kind) {
    case 'clip':
      out.push({ clip: node.clip, weight, timeScale: node.timeScale ? node.timeScale(p) : 1 });
      return;
    case 'blend1d': {
      const v = node.param(p);
      const pts = node.points;
      if (v <= pts[0]!.at) {
        evalNode(pts[0]!.node, `${path}.0`, rt, p, dt, weight, out);
        return;
      }
      const last = pts[pts.length - 1]!;
      if (v >= last.at) {
        evalNode(last.node, `${path}.${pts.length - 1}`, rt, p, dt, weight, out);
        return;
      }
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        if (v >= a.at && v <= b.at) {
          const t = (v - a.at) / (b.at - a.at);
          evalNode(a.node, `${path}.${i}`, rt, p, dt, weight * (1 - t), out);
          evalNode(b.node, `${path}.${i + 1}`, rt, p, dt, weight * t, out);
          return;
        }
      }
      return;
    }
    case 'select': {
      const chosen = node.choose(p);
      const fade = node.fades?.[chosen] ?? 0.15;
      const rate = fade <= 1e-4 ? 1 : Math.min(1, dt / fade);
      let sum = 0;
      const keys = Object.keys(node.children);
      for (const key of keys) {
        const id = `${path}:${key}`;
        const current = rt.selectWeights.get(id) ?? (key === chosen ? 1 : 0);
        const target = key === chosen ? 1 : 0;
        const next = current + (target - current) * rate;
        rt.selectWeights.set(id, next < 1e-3 && target === 0 ? 0 : next);
        sum += rt.selectWeights.get(id)!;
      }
      for (const key of keys) {
        const w = rt.selectWeights.get(`${path}:${key}`)! / (sum || 1);
        evalNode(node.children[key]!, `${path}/${key}`, rt, p, dt, weight * w, out);
      }
      return;
    }
  }
}

export function evaluate(
  root: GraphNode,
  rt: GraphRuntime,
  p: AnimParams,
  dt: number,
): PoseTask[] {
  const out: PoseTask[] = [];
  evalNode(root, '$', rt, p, dt, 1, out);
  return out;
}

// ── One-shot slots (montage equivalent) ──────────────────────────────────────

export interface SlotPlay {
  clip: string;
  fadeIn: number;
  fadeOut: number;
  /** Total seconds the slot holds (incl. fades); usually the clip duration. */
  duration: number;
  timeScale?: number;
}

export interface SlotState {
  playing: SlotPlay | null;
  elapsed: number;
}

export const createSlot = (): SlotState => ({ playing: null, elapsed: 0 });

export function slotPlay(slot: SlotState, play: SlotPlay): void {
  slot.playing = play;
  slot.elapsed = 0;
}

/** Advance and return the slot's task (weight envelope), or null when idle/finished. */
export function slotStep(slot: SlotState, dt: number): PoseTask | null {
  if (!slot.playing) return null;
  slot.elapsed += dt;
  const s = slot.playing;
  if (slot.elapsed >= s.duration) {
    slot.playing = null;
    return null;
  }
  let w = 1;
  if (slot.elapsed < s.fadeIn) w = slot.elapsed / s.fadeIn;
  const tailStart = s.duration - s.fadeOut;
  if (slot.elapsed > tailStart) w = Math.min(w, (s.duration - slot.elapsed) / s.fadeOut);
  return { clip: s.clip, weight: Math.max(0, Math.min(1, w)), timeScale: s.timeScale ?? 1 };
}

/** Compose base tasks under an active slot: slot overrides by its weight. */
export function compose(base: PoseTask[], slot: PoseTask | null): PoseTask[] {
  if (!slot) return base;
  return [...base.map((t) => ({ ...t, weight: t.weight * (1 - slot.weight) })), slot];
}
